// If you want to use Phoenix channels, run`mix help phx.gen.channel`
// to get started and then uncomment the line below.
// import "./user_socket.js"

// You can include dependencies in two ways.
//
// The simplest option is to put them in assets/vendor and
// import them using relative paths:
//
//     import "../vendor/some-package.js"
//
// Alternatively, you can `npm install some-package --prefix assets` and import
// them using a path starting with the package name:
//
//     import "some-package"
//

import "phoenix_html";
import { Socket } from "phoenix";
import { LiveSocket } from "phoenix_live_view";
import topbar from "../vendor/topbar";
import "./user_socket.js";
import Alpine from "alpinejs";
import "./theme_switcher";
import live_select from "live_select";

// ============================== HOOKS ==============================
import YouTubeVideo from "./hooks/youtube/video";
import YouTubePlaylist from "./hooks/youtube/playlist";
import YoutubeShorts from "./hooks/youtube/shorts";
import ScrollViewport from "./hooks/scroll_viewport";
import Flash from "./hooks/flash";
import FilterCollections from "./hooks/filter_collections";
import ShareInDetail from "./hooks/share_in_detail";
import ShareCollectionInList from "./hooks/share_collection_list";
import Pricing from "./hooks/subscriptions/pricing";
import ChangePlan from "./hooks/subscriptions/change_plan";
import CancelPlan from "./hooks/subscriptions/cancel_plan";
import ScrollTo from "./hooks/scroll_to";
import CopyUrlToClipboard from "./hooks/copy_url_to_clipboard";
import ContentEditor from "./hooks/editor/editor.js";
import ContentViewer from "./hooks/editor/viewer.js";
// ==============================

window.Alpine = Alpine;
Alpine.start();
let csrfToken = document
  .querySelector("meta[name='csrf-token']")
  .getAttribute("content");

let hooks = {
  ...live_select,
  YouTubeVideo: YouTubeVideo,
  YouTubePlaylist: YouTubePlaylist,
  YoutubeShorts: YoutubeShorts,
  ScrollViewport: ScrollViewport,
  Flash: Flash,
  FilterCollections: FilterCollections,
  ShareInDetail: ShareInDetail,
  ShareCollectionInList: ShareCollectionInList,
  // Onboarding: Onboarding,
  Pricing: Pricing,
  ChangePlan: ChangePlan,
  CancelPlan: CancelPlan,
  // TrixHandler: TrixHandler,
  ScrollTo: ScrollTo,
  CopyUrlToClipboard: CopyUrlToClipboard,
  ContentEditor: ContentEditor,
  ContentViewer: ContentViewer
};

let liveSocket = new LiveSocket("/live", Socket, {
  hooks: hooks,
  longPollFallbackMs: location.host.startsWith("localhost") ? undefined : 3000,
  dom: {
    onBeforeElUpdated(from, to) {
      if (from._x_dataStack) {
        window.Alpine.clone(from, to);
      }
    },
  },
  params: { _csrf_token: csrfToken },
});

topbar.config({
  barColors: { 0: "#AB2E94" },
  shadowColor: "rgba(0, 0, 0, .3)",
});

let topBarScheduled = undefined;
window.addEventListener("phx:page-loading-start", (_info) => {
  if (!topBarScheduled) {
    topBarScheduled = setTimeout(() => topbar.show(), 350);
  }
});
window.addEventListener("phx:page-loading-stop", (_info) => {
  clearTimeout(topBarScheduled);
  topBarScheduled = undefined;
  topbar.hide();
});

// connect if there are any LiveViews on the page
liveSocket.connect();

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket;
document.addEventListener("DOMContentLoaded", (_event) => {
  document.documentElement.lang = navigator.language || navigator.userLanguage;
});

// Back to top button functionality
const backToTopButton = document.querySelector('.back-to-top');
if (backToTopButton) {
  window.addEventListener('scroll', () => {
    if (window.scrollY > 300) {
      backToTopButton.classList.add('visible');
    } else {
      backToTopButton.classList.remove('visible');
    }
  });

  backToTopButton.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
}

// Mobile menu functionality
const mobileMenuButton = document.getElementById('mobile-menu-button');
const closeMobileMenuButton = document.getElementById('close-mobile-menu');
const mobileMenu = document.getElementById('mobile-menu');

if (mobileMenuButton && closeMobileMenuButton && mobileMenu) {
  const mobileMenuLinks = mobileMenu.getElementsByTagName('a');

  function toggleMobileMenu() {
    mobileMenu.classList.toggle('active');
    document.body.classList.toggle('overflow-hidden');
    mobileMenuButton.setAttribute(
      'aria-expanded',
      mobileMenuButton.getAttribute('aria-expanded') === 'false' ? 'true' : 'false'
    );
  }

  mobileMenuButton.addEventListener('click', toggleMobileMenu);
  closeMobileMenuButton.addEventListener('click', toggleMobileMenu);

  // Close mobile menu when clicking a link
  Array.from(mobileMenuLinks).forEach(link => {
    link.addEventListener('click', toggleMobileMenu);
  });
}

// Video Modal Functionality
function initVideoModal() {
  const videoModal = document.getElementById('demo-modal');
  const watchDemoButton = Array.from(document.querySelectorAll('button')).find(button =>
    button.textContent.trim() === 'Watch Demo'
  );
  const videoIframe = videoModal?.querySelector('iframe');

  if (videoModal && watchDemoButton && videoIframe) {
    // Store the original video URL
    const originalVideoUrl = videoIframe.getAttribute('data-src');

    // Open video modal
    watchDemoButton.addEventListener('click', () => {
      if (originalVideoUrl) {
        videoIframe.src = originalVideoUrl;
        videoModal.classList.add('active');
        document.body.classList.add('overflow-hidden');
      }
    });

    // Close video modal
    const closeVideoModal = () => {
      videoIframe.src = '';
      videoModal.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
    };

    // Close on backdrop click
    videoModal.addEventListener('click', (e) => {
      if (e.target === videoModal) {
        closeVideoModal();
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && videoModal.classList.contains('active')) {
        closeVideoModal();
      }
    });

    // Add closeModal function to window for onclick handlers
    window.closeModal = closeVideoModal;
  }
}

// Image Modal Functionality
document.addEventListener('DOMContentLoaded', () => {
  initVideoModal();

  // FAQ Category Buttons
  const faqCategoryButtons = document.querySelectorAll('[data-scroll-to]');
  faqCategoryButtons.forEach(button => {
    button.addEventListener('click', () => {
      faqCategoryButtons.forEach(btn => btn.classList.remove('active'));
      button.classList.add('active');
      const targetId = button.getAttribute('data-scroll-to');
      const targetSection = document.getElementById(targetId);

      if (targetSection) {
        const headerHeight = document.querySelector('nav').offsetHeight;
        const targetPosition = targetSection.getBoundingClientRect().top + window.scrollY - headerHeight - 20;
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    });
  });

  // FAQ accordion functionality
  document.querySelectorAll('.faq-item').forEach(item => {
    const button = item.querySelector('button');
    if (button) {
      button.addEventListener('click', () => {
        const isActive = item.classList.contains('active');
        document.querySelectorAll('.faq-item').forEach(faqItem => {
          faqItem.classList.remove('active');
        });
        if (!isActive) {
          item.classList.add('active');
        }
      });
    }
  });

  // Create modal container if it doesn't exist
  if (!document.querySelector('.image-modal')) {
    const modalContainer = document.createElement('div');
    modalContainer.className = 'image-modal fixed inset-0 bg-stone-950/90 z-50 hidden items-center justify-center backdrop-blur-sm';
    modalContainer.innerHTML = `
            <button class="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full transition" aria-label="Close modal">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
            <img class="max-h-[90vh] max-w-[90vw] object-contain" src="" alt="Modal image">
        `;
    document.body.appendChild(modalContainer);
  }

  const imageModal = document.querySelector('.image-modal');
  const modalImg = imageModal.querySelector('img');

  // Add click events to all images that should open in modal
  document.querySelectorAll('img.open-modal').forEach(img => {

    img.addEventListener('click', () => {
      modalImg.src = img.src;
      modalImg.alt = img.alt;
      imageModal.classList.remove('hidden');
      imageModal.classList.add('flex');
      document.body.classList.add('overflow-hidden');
    });
  });

  // Close modal functionality
  function closeImageModal() {
    imageModal.classList.remove('flex');
    imageModal.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
  }

  // Close on backdrop or button click
  imageModal.addEventListener('click', (e) => {
    if (e.target === imageModal || e.target.closest('button')) {
      closeImageModal();
    }
  });

  // Close on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !imageModal.classList.contains('hidden')) {
      closeImageModal();
    }
  });
});

// Keyboard navigation
document.addEventListener('keydown', function (e) {
  // Add Home key support for back to top
  if (e.key === 'Home') {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
});

// Pricing Toggle (if exists)
const pricingToggle = document.querySelector('[role="switch"]');
if (pricingToggle) {
  pricingToggle.addEventListener('click', () => {
    const isAnnual = pricingToggle.getAttribute('aria-checked') === 'true';
    pricingToggle.setAttribute('aria-checked', !isAnnual);
    pricingToggle.querySelector('span').style.transform = isAnnual ? 'translateX(0)' : 'translateX(32px)';
    // Add price update logic here
  });
}

// Intersection Observer for animations
const observerOptions = {
  threshold: 0.1,
  rootMargin: '50px'
};

const observer = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      entry.target.style.opacity = '1';
      observer.unobserve(entry.target);
    }
  });
}, observerOptions);

document.querySelectorAll('.animate-fade-in-up').forEach(el => {
  el.style.opacity = '0';
  observer.observe(el);
});
